import { useState } from 'react';
import {
  WINDOW_PRESETS,
  type BrowserProfileDto,
  type UpdateBrowserProfileBody,
} from '@br/protocol';
import { errorMessage } from '../api/client';
import { useUpdateProfile } from '../api/queries';
import { Alert, Button, Checkbox, Field, Input, Modal, Select } from '../components/ui';
import { LOCALE_RE, PROXY_RE, RegionFields, isKnownTimezone } from './ProfileCreateDialog';

type HeadlessChoice = 'inherit' | 'on' | 'off';
type IdleChoice = 'inherit' | 'never' | 'custom';

interface FormState {
  name: string;
  customerLabel: string;
  size: string;
  customWidth: string;
  customHeight: string;
  headless: HeadlessChoice;
  proxyEnabled: boolean;
  proxyServer: string;
  proxyUsername: string;
  proxyPassword: string;
  clearPassword: boolean;
  proxyBypass: string;
  locale: string;
  timezoneId: string;
  idle: IdleChoice;
  idleMinutes: string;
}

function fromProfile(profile: BrowserProfileDto): FormState {
  const preset = WINDOW_PRESETS.find(
    (item) => item.width === profile.size.width && item.height === profile.size.height,
  );
  return {
    name: profile.name,
    customerLabel: profile.customerLabel,
    size: preset ? `${preset.width}x${preset.height}` : 'custom',
    customWidth: String(profile.size.width),
    customHeight: String(profile.size.height),
    headless: profile.headless === null ? 'inherit' : profile.headless ? 'on' : 'off',
    proxyEnabled: Boolean(profile.proxy),
    proxyServer: profile.proxy?.server ?? '',
    proxyUsername: profile.proxy?.username ?? '',
    proxyPassword: '',
    clearPassword: false,
    proxyBypass: profile.proxy?.bypass ?? '',
    locale: profile.locale ?? '',
    timezoneId: profile.timezoneId ?? '',
    idle: profile.idleMinutes === null ? 'inherit' : profile.idleMinutes === 0 ? 'never' : 'custom',
    idleMinutes: String(profile.idleMinutes && profile.idleMinutes > 0 ? profile.idleMinutes : 30),
  };
}

function presetLabel(label: string): string {
  return label
    .replace('Desktop', '데스크톱')
    .replace('Narrow', '좁은 화면')
    .replace('Mobile-ish', '모바일형');
}

export function ProfileEditDialog({
  open,
  profile,
  onClose,
}: {
  open: boolean;
  profile: BrowserProfileDto;
  onClose: () => void;
}) {
  const update = useUpdateProfile(profile.id);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const f = form ?? fromProfile(profile);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm({ ...f, [k]: v });
  const close = () => {
    setForm(null);
    setError(null);
    onClose();
  };

  const submit = () => {
    setError(null);
    if (!f.name.trim()) return setError('프로필 이름을 입력하세요');
    const locale = f.locale.trim();
    const timezoneId = f.timezoneId.trim();
    if (locale && !LOCALE_RE.test(locale))
      return setError('언어 형식이 올바르지 않습니다 (예: en-US)');
    if (timezoneId && !isKnownTimezone(timezoneId))
      return setError('시간대 형식이 올바르지 않습니다 (예: America/New_York)');
    const idleMinutes = Number(f.idleMinutes);
    if (
      f.idle === 'custom' &&
      (!Number.isInteger(idleMinutes) || idleMinutes < 1 || idleMinutes > 1440)
    )
      return setError('자동 종료 시간은 1~1440분 사이의 정수여야 합니다');
    const body: UpdateBrowserProfileBody = {
      name: f.name.trim(),
      customerLabel: f.customerLabel.trim(),
      headless: f.headless === 'inherit' ? null : f.headless === 'on',
      locale: locale || null,
      timezoneId: timezoneId || null,
      idleMinutes: f.idle === 'inherit' ? null : f.idle === 'never' ? 0 : idleMinutes,
    };
    if (f.size === 'custom') {
      const w = Number(f.customWidth);
      const h = Number(f.customHeight);
      if (!(w >= 320 && w <= 3840 && h >= 240 && h <= 2160))
        return setError('창 크기는 가로 320~3840, 세로 240~2160 사이여야 합니다');
      body.size = { width: w, height: h };
    } else {
      const [w, h] = f.size.split('x').map(Number);
      if (w && h) body.size = { width: w, height: h };
    }
    if (f.proxyEnabled) {
      if (!PROXY_RE.test(f.proxyServer.trim()))
        return setError('프록시 주소 형식이 올바르지 않습니다 (예: http://host:port)');
      body.proxy = {
        server: f.proxyServer.trim(),
        username: f.proxyUsername.trim() || undefined,
        // undefined keeps the stored password, '' clears it, anything else replaces it
        password: f.clearPassword ? '' : f.proxyPassword || undefined,
        bypass: f.proxyBypass.trim() || undefined,
      };
    } else if (profile.proxy) {
      body.proxy = null;
    }
    update.mutate(body, { onSuccess: close, onError: (err) => setError(errorMessage(err)) });
  };

  return (
    <Modal
      open={open}
      title="프로필 편집"
      onClose={close}
      size="lg"
      footer={
        <>
          <Button onClick={close}>취소</Button>
          <Button variant="primary" onClick={submit} loading={update.isPending}>
            저장
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="프로필 이름" required>
          <Input value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="고객 라벨">
          <Input value={f.customerLabel} onChange={(e) => set('customerLabel', e.target.value)} />
        </Field>
        <Field
          label="창 크기"
          hint="브라우저가 열려 있으면 다음에 열 때 적용됩니다. 지금 바꾸려면 뷰어에서 조절하세요"
        >
          <Select value={f.size} onChange={(e) => set('size', e.target.value)}>
            {WINDOW_PRESETS.map((p) => (
              <option key={p.label} value={`${p.width}x${p.height}`}>
                {presetLabel(p.label)}
              </option>
            ))}
            <option value="custom">직접 입력</option>
          </Select>
        </Field>
        <Field label="headless">
          <Select
            value={f.headless}
            onChange={(e) => set('headless', e.target.value as HeadlessChoice)}
          >
            <option value="inherit">노드 설정 따름</option>
            <option value="off">끔 (창 표시)</option>
            <option value="on">켬</option>
          </Select>
        </Field>
        {f.size === 'custom' && (
          <div className="grid grid-cols-2 gap-3 sm:col-span-2">
            <Field label="가로 (px)">
              <Input
                type="number"
                min={320}
                max={3840}
                value={f.customWidth}
                onChange={(e) => set('customWidth', e.target.value)}
              />
            </Field>
            <Field label="세로 (px)">
              <Input
                type="number"
                min={240}
                max={2160}
                value={f.customHeight}
                onChange={(e) => set('customHeight', e.target.value)}
              />
            </Field>
          </div>
        )}
        <div className="sm:col-span-2 rounded-lg border border-line p-3">
          <Checkbox
            label="HTTP 프록시 사용"
            checked={f.proxyEnabled}
            onChange={(e) => set('proxyEnabled', e.target.checked)}
          />
          {profile.lastOpenedAt && (
            <p className="mt-2 text-xs text-ink-muted">
              프록시를 바꾸면 기존 브라우저 데이터(쿠키·로그인 상태)가 다른 IP에서 사용됩니다. 출구
              IP가 달라진다면 브라우저 데이터를 삭제한 뒤 여는 편이 안전합니다.
            </p>
          )}
          {f.proxyEnabled && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="서버" required className="sm:col-span-2">
                <Input
                  value={f.proxyServer}
                  onChange={(e) => set('proxyServer', e.target.value)}
                  placeholder="http://proxy.example.com:8080"
                />
              </Field>
              <Field label="사용자 이름">
                <Input
                  value={f.proxyUsername}
                  onChange={(e) => set('proxyUsername', e.target.value)}
                  autoComplete="off"
                />
              </Field>
              <Field
                label="비밀번호"
                hint={
                  profile.proxy?.hasPassword ? '비워두면 저장된 비밀번호를 유지합니다' : undefined
                }
              >
                <Input
                  type="password"
                  value={f.proxyPassword}
                  disabled={f.clearPassword}
                  onChange={(e) => set('proxyPassword', e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
              {profile.proxy?.hasPassword && (
                <Checkbox
                  className="sm:col-span-2"
                  label="저장된 비밀번호 삭제"
                  checked={f.clearPassword}
                  onChange={(e) => set('clearPassword', e.target.checked)}
                />
              )}
              <Field label="우회 목록" hint="쉼표로 구분" className="sm:col-span-2">
                <Input value={f.proxyBypass} onChange={(e) => set('proxyBypass', e.target.value)} />
              </Field>
            </div>
          )}
        </div>
        <RegionFields
          locale={f.locale}
          timezoneId={f.timezoneId}
          onLocale={(v) => set('locale', v)}
          onTimezone={(v) => set('timezoneId', v)}
        />
        <Field
          label="접속자가 없을 때 자동 종료"
          hint={`현재 적용값은 ${profile.effectiveIdleMinutes === 0 ? '자동 종료 안 함' : `${profile.effectiveIdleMinutes}분`}입니다. Chrome 프로세스만 닫고 프로필 데이터는 삭제하지 않습니다.`}
          className="sm:col-span-2"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Select value={f.idle} onChange={(e) => set('idle', e.target.value as IdleChoice)}>
              <option value="inherit">서버 기본값 사용</option>
              <option value="never">자동 종료 안 함</option>
              <option value="custom">직접 지정</option>
            </Select>
            {f.idle === 'custom' && (
              <Input
                type="number"
                min={1}
                max={1440}
                value={f.idleMinutes}
                onChange={(e) => set('idleMinutes', e.target.value)}
                aria-label="자동 종료 시간(분)"
                placeholder="분"
              />
            )}
          </div>
        </Field>
        {error && (
          <div className="sm:col-span-2">
            <Alert level="error">{error}</Alert>
          </div>
        )}
      </div>
    </Modal>
  );
}
